import React from 'react';
import { useNavigate } from 'react-router-dom';

import { UxContext } from '@client/context/UxContext';
import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import { getSocketIo } from '@client/store/globalStore/init/socketIo';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { TitleAndDescription } from '@ledium/ui-common';
import StopIcon from '@mui/icons-material/Stop';
import { Container, Grid } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';

import DashboardProject, { DashboardProjectItem, ProjectSocketData } from './Components/DashboardProject';

interface ZoneInfo {
    id: string;
    virtual?: true;
    virtualSource?: { zoneId: string; type: string };
}

const Dashboard = () => {
    const navigate = useNavigate();
    const { handleSnackbar } = React.useContext(UxContext)!;
    const {
        globalState: { dashboardGroupId },
        globalActions
    } = useGlobalStore();

    const [loading, setLoading] = React.useState(true);
    const [projectList, setProjectList] = React.useState<DashboardProjectItem[]>([]);
    const [projectData, setProjectData] = React.useState<Record<string, ProjectSocketData | null>>({});
    const [zones, setZones] = React.useState<ZoneInfo[]>([]);
    const [stopAllDialogOpen, setStopAllDialogOpen] = React.useState(false);

    const socket = getSocketIo();
    React.useEffect(() => {
        function handleProjectStatus(data: ProjectSocketData) {
            setProjectData((prev) => ({
                ...prev,
                [data.projectId]: data
            }));
        }

        socket.on('project_status', handleProjectStatus);

        return () => {
            socket.off('project_status', handleProjectStatus);
        };
    }, [socket]);

    const refreshProjects = React.useCallback(function refreshProjects() {
        lediumApi.get<DashboardProjectItem[]>('projects/getDashboard/').then((projects) => {
            if (projects) {
                setProjectList(projects);
            }
        });
    }, []);

    function onClickAddNewProject() {
        navigate('/projects/new');
    }

    const handleStopProject = React.useCallback(
        function handleStopProject(projectId: string) {
            setProjectData((prev) => ({
                ...prev,
                [projectId]: null
            }));
            refreshProjects();
        },
        [refreshProjects]
    );

    const runningProjects = projectList.filter((p) => p.running);

    async function handleStopAll() {
        setStopAllDialogOpen(false);
        let hasError = false;
        for (const project of runningProjects) {
            const result = await lediumApi.get(`pRunner/stopProject/${project.id}`);
            if (result) {
                setProjectData((prev) => ({ ...prev, [project.id]: null }));
            } else {
                hasError = true;
            }
        }
        refreshProjects();
        if (hasError) {
            handleSnackbar({ message: i18n('project_action_failed') as string, severity: 'error' });
        } else {
            handleSnackbar({ message: i18n('all_projects_stopped') as string, severity: 'warning' });
        }
    }

    React.useEffect(() => {
        lediumApi.get<DashboardProjectItem[]>('projects/getDashboard/').then((projects) => {
            if (projects) {
                setProjectList(projects);
            }
            setLoading(false);
        });
        lediumApi.get<ZoneInfo[]>('zones').then((data) => {
            if (data) {
                setZones(data.filter((z) => z.id !== 'root'));
            }
        });
        globalActions.zones.setDashboardGroup(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const mirrorZones = dashboardGroupId ? zones.filter((z) => z.virtual && z.virtualSource?.zoneId === dashboardGroupId) : [];

    const filteredProjects = dashboardGroupId
        ? projectList.filter((p) => p.zoneId === dashboardGroupId || mirrorZones.some((mz) => mz.id === p.zoneId))
        : projectList;

    function renderProjects() {
        if (dashboardGroupId && mirrorZones.length === 0) {
            return filteredProjects.map((project) => (
                <Grid item xs={12} key={project.id}>
                    <DashboardProject
                        project={project}
                        projectData={projectData[project.id]}
                        refreshProjects={refreshProjects}
                        handleStopProject={handleStopProject}
                    />
                </Grid>
            ));
        }

        const groups = new Map<string, typeof filteredProjects>();
        for (const project of filteredProjects) {
            const label = `Zone: ${project.group?.name ?? 'Unknown'}`;
            const group = groups.get(label);
            if (group) {
                group.push(project);
            } else {
                groups.set(label, [project]);
            }
        }
        const sorted = Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));

        return sorted.map(([label, projects]) => (
            <React.Fragment key={label}>
                <Grid item xs={12}>
                    <Typography variant="h5" sx={{ mt: 1, mb: 0.5, color: 'text.secondary', fontWeight: 200 }}>
                        {label}
                    </Typography>
                </Grid>
                {projects.map((project) => (
                    <Grid item xs={12} key={project.id}>
                        <DashboardProject
                            project={project}
                            projectData={projectData[project.id]}
                            refreshProjects={refreshProjects}
                            handleStopProject={handleStopProject}
                        />
                    </Grid>
                ))}
            </React.Fragment>
        ));
    }

    return (
        <Container>
            <Dialog open={stopAllDialogOpen} onClose={() => setStopAllDialogOpen(false)}>
                <DialogTitle>{i18n('stop_all_running_projects')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{i18n('stop_all_running_confirm', runningProjects.length)}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStopAllDialogOpen(false)}>{i18n('cancel')}</Button>
                    <Button variant="contained" color="error" onClick={handleStopAll}>
                        {i18n('stop_all_running_projects')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Grid container spacing={3}>
                <TitleAndDescription
                    titleAction={
                        runningProjects.length > 0 ? (
                            <Button sx={{ mt: '30px' }} variant="contained" color="error" startIcon={<StopIcon />} onClick={() => setStopAllDialogOpen(true)}>
                                {i18n('stop_all_running_projects')}
                            </Button>
                        ) : null
                    }
                />

                {!filteredProjects.length && !loading ? (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="body1">{i18n('no_project')}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="contained" onClick={onClickAddNewProject}>
                                {i18n('add_new_project')}
                            </Button>
                        </Grid>
                    </>
                ) : null}
                {renderProjects()}
            </Grid>
        </Container>
    );
};

export default React.memo(Dashboard);
